import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type ICellRendererParams,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { Check, ChevronDown, Copy, Plus, Search } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import {
  getProduitsTypesList,
  getProduitsCategoriesList,
  getProduitsUnitesMesureList,
  getProduitsMedicaments,
  createProduit,
  updateProduit,
  deleteProduit,
  type ProduitMedicament,
  type ProduitPrix,
  type ProduitStatut,
  type ProduitTypeController,
} from "../../../api/etablissement";
import { useAuth } from "../../../store/authEtablissementStore";

ModuleRegistry.registerModules([AllCommunityModule]);

/** Variables CSS AG-Grid mappées sur les tokens shadcn/ui du projet */
const AG_SHADCN_VARS: React.CSSProperties = {
  "--ag-background-color": "hsl(var(--background))",
  "--ag-foreground-color": "hsl(var(--foreground))",
  "--ag-secondary-foreground-color": "hsl(var(--muted-foreground))",
  "--ag-header-background-color": "#e5e7eb",
  "--ag-header-foreground-color": "#111827",
  "--ag-border-color": "hsl(var(--border))",
  "--ag-row-border-color": "hsl(var(--border))",
  "--ag-odd-row-background-color": "hsl(var(--background))",
  "--ag-even-row-background-color": "hsl(var(--background))",
  "--ag-row-hover-color": "#f0faf5",
  "--ag-selected-row-background-color": "#d1e2dd",
  "--ag-column-hover-color": "#f0faf5",
  "--ag-range-selection-border-color": "#00553E",
  "--ag-checkbox-checked-color": "#00553E",
  "--ag-input-focus-border-color": "#00553E",
  "--ag-font-family": "Inter, sans-serif",
  "--ag-font-size": "0.875rem",
  "--ag-cell-horizontal-padding": "16px",
  "--ag-wrapper-border-radius": "var(--radius)",
  "--ag-card-radius": "var(--radius)",
  "--ag-header-height": "48px",
  "--ag-row-height": "56px",
  "--ag-pagination-background-color": "hsl(var(--background))",
  "--ag-header-column-separator-display": "block",
  "--ag-header-column-separator-color": "#d1d5db",
  "--ag-header-column-separator-height": "50%",
} as React.CSSProperties;

// ─── Types ──────────────────────────────────────────────────────────────────

type Product = {
  id: string;
  name: string;
  qty: number;
  type?: ProduitTypeController;
  categorie?: string;
  unite?: string;
  prixAchat?: number;
  prixVente?: number;
  priceLabel: string;
  date: string;
  seuilAlerte?: number;
  quantiteReservee: number;
  datePeremption?: string;
  statut?: ProduitStatut;
};

type DraftProduit = {
  id: string;
  nom: string;
  quantite: string;
  type: string;
  categorie: string;
  unite: string;
  prixAchat: string;
  prixVente: string;
  dateMaj: string;
  datePeremption: string;
};

type GridContext = {
  onEdit: (p: Product) => void;
  onDelete: (id: string) => void;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPrix(prix: ProduitPrix | unknown): string {
  if (prix == null) return "";
  if (typeof prix === "object" && prix !== null && "prixVente" in prix) {
    const n = Number((prix as ProduitPrix).prixVente);
    if (!Number.isFinite(n)) return "";
    return `${n.toLocaleString("fr-FR")} FCFA`;
  }
  if (typeof prix === "object" && prix !== null && "prixUnitaire" in prix) {
    const n = Number((prix as ProduitPrix).prixUnitaire);
    if (!Number.isFinite(n)) return "";
    return `${n.toLocaleString("fr-FR")} FCFA`;
  }
  if (typeof prix === "string") return prix;
  if (typeof prix === "number" && Number.isFinite(prix))
    return `${prix.toLocaleString("fr-FR")} FCFA`;
  return "";
}

function formatDateMAJ(value: unknown): string {
  if (value == null || value === "") return "";
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function apiToProduct(p: ProduitMedicament): Product {
  const id = (p.id ?? "").toString();
  const rawDate = p.dateMaj ?? p.date ?? p.dateModification ?? p.dateCreation;
  const prixRaw = (p.prix ?? p.price) as any;
  const achat =
    prixRaw && typeof prixRaw === "object" && "achat" in prixRaw
      ? Number(prixRaw.achat)
      : undefined;
  const vente =
    prixRaw && typeof prixRaw === "object" && "vente" in prixRaw
      ? Number(prixRaw.vente)
      : undefined;
  return {
    id,
    name: (p.nom ?? p.name ?? "").toString(),
    qty: Number(p.quantite ?? p.qty) || 0,
    type: (p as any).type,
    categorie: (p as any).categorie,
    unite: (p as any).unite,
    prixAchat: Number.isFinite(achat) ? achat : undefined,
    prixVente: Number.isFinite(vente) ? vente : undefined,
    priceLabel: formatPrix(p.prix ?? p.price),
    date: formatDateMAJ(rawDate) || (rawDate != null ? String(rawDate) : ""),
    seuilAlerte: p.seuilAlerte ?? p.stockMinimum,
    quantiteReservee: Number(p.quantiteReservee ?? 0),
    datePeremption: p.datePeremption,
    statut: p.statut,
  };
}

function makeEmptyDraft(): DraftProduit {
  return {
    id: "",
    nom: "",
    quantite: "0",
    type: "",
    categorie: "",
    unite: "",
    prixAchat: "",
    prixVente: "",
    dateMaj: new Date().toLocaleDateString("fr-FR"),
    datePeremption: "",
  };
}

function isFiniteNumberString(s: string): boolean {
  const trimmed = String(s ?? "").trim();
  if (!trimmed) return false;
  return Number.isFinite(Number(trimmed.replace(",", ".")));
}

function toNumber(s: string): number {
  return Number(String(s).trim().replace(",", "."));
}

// ─── Cell Renderers ───────────────────────────────────────────────────────────

const TYPE_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  medicament:  { bg: "bg-blue-100",   text: "text-blue-700",   label: "Médicament" },
  materiel:    { bg: "bg-orange-100", text: "text-orange-700", label: "Matériel" },
  consommable: { bg: "bg-teal-100",   text: "text-teal-700",   label: "Consommable" },
  equipement:  { bg: "bg-purple-100", text: "text-purple-700", label: "Équipement" },
};

function useCopy(text: string) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);
  return { copied, copy };
}

function CopyBtn({ text }: { text: string }) {
  const { copied, copy } = useCopy(text);
  return (
    <button
      onClick={copy}
      title="Copier"
      className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 rounded p-0.5 hover:bg-black/5 text-muted-foreground"
    >
      {copied
        ? <Check size={12} className="text-green-600" />
        : <Copy size={12} />}
    </button>
  );
}

function CopyCellRenderer({ value, valueFormatted }: ICellRendererParams) {
  const text = valueFormatted ?? (value != null ? String(value) : "");
  if (!text) return null;
  return (
    <div className="group flex items-center justify-between h-full w-full gap-1">
      <span className="truncate">{text}</span>
      <CopyBtn text={text} />
    </div>
  );
}

function TypeBadgeCellRenderer({ value }: ICellRendererParams<Product>) {
  const v = (value ?? "").toString().toLowerCase();
  const badge = TYPE_BADGE[v];
  const label = badge?.label ?? value ?? "";
  return (
    <div className="group flex items-center justify-between h-full gap-1">
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
          badge ? `${badge.bg} ${badge.text}` : "bg-muted text-muted-foreground"
        }`}
      >
        {label}
      </span>
      <CopyBtn text={label} />
    </div>
  );
}

const STATUT_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  disponible:   { bg: "bg-green-100", text: "text-green-700",  label: "Disponible" },
  alerte:       { bg: "bg-amber-100", text: "text-amber-700",  label: "Alerte" },
  rupture_stock:{ bg: "bg-red-100",   text: "text-red-700",    label: "Rupture" },
  expire:       { bg: "bg-gray-100",  text: "text-gray-500",   label: "Expiré" },
  hors_service: { bg: "bg-gray-100",  text: "text-gray-500",   label: "Hors service" },
};

function StatutBadgeCellRenderer({ value }: ICellRendererParams<Product>) {
  const v = (value ?? "").toString();
  const badge = STATUT_BADGE[v];
  const label = badge?.label ?? v;
  if (!label) return null;
  return (
    <div className="group flex items-center justify-between h-full gap-1">
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
          badge ? `${badge.bg} ${badge.text}` : "bg-muted text-muted-foreground"
        }`}
      >
        {label}
      </span>
      <CopyBtn text={label} />
    </div>
  );
}

function QtyCellRenderer({ value, data }: ICellRendererParams<Product>) {
  const n = Number(value ?? 0);
  const seuil = data?.seuilAlerte ?? 10;
  const text = n.toLocaleString("fr-FR");
  const colorCls =
    n === 0      ? "text-red-600 font-semibold" :
    n <= seuil   ? "text-amber-600 font-medium" :
    "";
  return (
    <div className="group flex items-center justify-between h-full gap-1">
      <span className={colorCls}>{text}</span>
      <CopyBtn text={text} />
    </div>
  );
}

function ActionsCellRenderer({
  data,
  context,
}: ICellRendererParams<Product, unknown, GridContext>) {
  if (!data) return null;
  return (
    <div className="flex items-center gap-2 h-full">
      <Button
        variant="outline"
        size="sm"
        onClick={() => context.onEdit(data)}
      >
        Modifier
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/5"
        onClick={() => context.onDelete(data.id)}
      >
        Supprimer
      </Button>
    </div>
  );
}

function PrixCellRenderer({ value, data }: ICellRendererParams<Product>) {
  const text =
    value != null && Number.isFinite(value)
      ? `${(value as number).toLocaleString("fr-FR")} FCFA`
      : data?.priceLabel ?? "";
  if (!text) return null;
  return (
    <div className="group flex items-center justify-between h-full gap-1">
      <span>{text}</span>
      <CopyBtn text={text} />
    </div>
  );
}

// ─── Classes utilitaires (style shadcn/ui) ────────────────────────────────────

const inputCls =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

const selectCls =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

// ─── Composant principal ──────────────────────────────────────────────────────

export default function ProductsPage() {
  const { establishment } = useAuth();
  const effectiveEtablissementId = useMemo(() => {
    const id = establishment?.id;
    if (typeof id !== "string") return "";
    return id.trim();
  }, [establishment?.id]);

  const [period, setPeriod] = useState<"7" | "30" | "90">("30");
  const [order, setOrder] = useState<"recent" | "oldest" | "name">("recent");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<DraftProduit>(makeEmptyDraft());

  const [typesList, setTypesList] = useState<
    Array<{ code?: string; nom?: string }>
  >([]);
  const [categoriesList, setCategoriesList] = useState<
    Array<{ code?: string; nom?: string }>
  >([]);
  const [unitesList, setUnitesList] = useState<
    Array<{ code?: string; nom?: string }>
  >([]);

  const gridRef = useRef<AgGridReact<Product>>(null);

  // ── Chargement ──────────────────────────────────────────────────────────────

  const loadProducts = useCallback(() => {
    if (!effectiveEtablissementId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    getProduitsMedicaments(effectiveEtablissementId)
      .then((res) => {
        if (res.ok) setProducts(res.data.map(apiToProduct));
        else setError(res.message || "Impossible de charger les produits.");
      })
      .catch(() => setError("Impossible de charger les produits."))
      .finally(() => setLoading(false));
  }, [effectiveEtablissementId]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getProduitsTypesList(),
      getProduitsCategoriesList(),
      getProduitsUnitesMesureList(),
    ])
      .then(([t, c, u]) => {
        if (cancelled) return;
        if (t.ok) setTypesList(t.data);
        if (c.ok) setCategoriesList(c.data);
        if (u.ok) setUnitesList(u.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Options des selects ──────────────────────────────────────────────────────

  const defaultTypes: ProduitTypeController[] = [
    "medicament",
    "materiel",
    "consommable",
    "equipement",
  ];
  const typesOptions = typesList.length
    ? typesList.map((x) => (x.code ?? x.nom ?? "").trim()).filter(Boolean)
    : defaultTypes;
  const categoriesOptions = categoriesList
    .map((x) => (x.code ?? x.nom ?? "").trim())
    .filter(Boolean);
  const unitesOptions = unitesList
    .map((x) => (x.code ?? x.nom ?? "").trim())
    .filter(Boolean);

  // ── Actions CRUD ─────────────────────────────────────────────────────────────

  const remove = useCallback(async (id: string) => {
    const res = await deleteProduit(id);
    if (res.ok) setProducts((prev) => prev.filter((p) => p.id !== id));
    else setError(res.message || "Erreur lors de la suppression.");
  }, []);

  const openAdd = useCallback(() => {
    setEditingProduct(null);
    setDraft(makeEmptyDraft());
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((p: Product) => {
    setEditingProduct(p);
    setDraft({
      id: p.id,
      nom: p.name,
      quantite: String(p.qty ?? 0),
      type: p.type ?? "",
      categorie: p.categorie ?? "",
      unite: p.unite ?? "",
      prixAchat:
        p.prixAchat != null && Number.isFinite(p.prixAchat)
          ? String(p.prixAchat)
          : "",
      prixVente:
        p.prixVente != null && Number.isFinite(p.prixVente)
          ? String(p.prixVente)
          : "",
      dateMaj: p.date || new Date().toLocaleDateString("fr-FR"),
      datePeremption: p.datePeremption ?? "",
    });
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingProduct(null);
  }, []);

  const saveModal = async () => {
    if (draft.nom.trim().length < 2) return setError("Nom requis (min. 2 caractères).");
    if (!draft.type) return setError("Type requis.");
    if (!draft.categorie || draft.categorie.trim().length < 2) return setError("Catégorie requise.");
    if (!draft.unite) return setError("Unité requise.");
    if (!isFiniteNumberString(draft.quantite)) return setError("Quantité invalide.");
    if (!isFiniteNumberString(draft.prixAchat) || !isFiniteNumberString(draft.prixVente))
      return setError("Prix achat/vente invalides (nombres uniquement).");

    setSaving(true);

    if (editingProduct) {
      const res = await updateProduit(editingProduct.id, {
        nom: draft.nom,
        type: draft.type,
        categorie: draft.categorie,
        quantite: toNumber(draft.quantite),
        unite: draft.unite,
        prix: { achat: toNumber(draft.prixAchat), vente: toNumber(draft.prixVente) },
        dateMaj: draft.dateMaj,
        datePeremption: draft.datePeremption || undefined,
      });
      setSaving(false);
      if (res.ok) {
        setProducts((prev) =>
          prev.map((p) =>
            p.id === editingProduct.id
              ? {
                  ...p,
                  name: draft.nom,
                  qty: toNumber(draft.quantite),
                  type: draft.type as ProduitTypeController,
                  categorie: draft.categorie,
                  unite: draft.unite,
                  prixAchat: toNumber(draft.prixAchat),
                  prixVente: toNumber(draft.prixVente),
                  priceLabel: `${toNumber(draft.prixVente).toLocaleString("fr-FR")} FCFA`,
                  date: draft.dateMaj,
                  datePeremption: draft.datePeremption || undefined,
                }
              : p
          )
        );
        closeModal();
      } else setError(res.message || "Erreur lors de la modification.");
    } else {
      if (!effectiveEtablissementId) return;
      const res = await createProduit({
        etablissementId: effectiveEtablissementId,
        nom: draft.nom,
        type: draft.type,
        categorie: draft.categorie,
        quantite: toNumber(draft.quantite),
        unite: draft.unite,
        prix: { achat: toNumber(draft.prixAchat), vente: toNumber(draft.prixVente) },
        dateMaj: draft.dateMaj,
        datePeremption: draft.datePeremption || undefined,
      });
      setSaving(false);
      if (res.ok) {
        if (res.data) setProducts((prev) => [apiToProduct(res.data!), ...prev]);
        else loadProducts();
        closeModal();
      } else setError(res.message || "Erreur lors de l'ajout.");
    }
  };

  // ── AG-Grid config ───────────────────────────────────────────────────────────

  const gridContext = useMemo<GridContext>(
    () => ({ onEdit: openEdit, onDelete: remove }),
    [openEdit, remove]
  );

  const colDefs = useMemo<ColDef<Product>[]>(
    () => [
      { field: "name",      headerName: "Nom",       flex: 2, minWidth: 150, cellRenderer: CopyCellRenderer },
      { field: "type",      headerName: "Type",      flex: 1, minWidth: 120, cellRenderer: TypeBadgeCellRenderer },
      { field: "categorie", headerName: "Catégorie", flex: 1, minWidth: 110, cellRenderer: CopyCellRenderer },
      { field: "unite",     headerName: "Unité",     flex: 1, minWidth: 80,  cellRenderer: CopyCellRenderer },
      { field: "qty",    headerName: "Quantité", flex: 1, minWidth: 100, cellRenderer: QtyCellRenderer },
      { field: "statut", headerName: "Statut",   flex: 1, minWidth: 120, cellRenderer: StatutBadgeCellRenderer },
      {
        field: "prixAchat",
        headerName: "Prix achat",
        flex: 1,
        minWidth: 140,
        valueFormatter: ({ value }) =>
          value != null && Number.isFinite(value)
            ? `${(value as number).toLocaleString("fr-FR")} FCFA`
            : "",
        cellRenderer: CopyCellRenderer,
      },
      {
        field: "prixVente",
        headerName: "Prix vente",
        flex: 1,
        minWidth: 140,
        cellRenderer: PrixCellRenderer,
      },
      { field: "datePeremption", headerName: "Péremption", flex: 1, minWidth: 110, cellRenderer: CopyCellRenderer },
      { field: "date",           headerName: "Date MAJ",   flex: 1, minWidth: 110, cellRenderer: CopyCellRenderer },
      {
        headerName: "Actions",
        minWidth: 185,
        sortable: false,
        filter: false,
        cellRenderer: ActionsCellRenderer,
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({ sortable: true, filter: true, resizable: true }),
    []
  );

  const rowData = useMemo(() => {
    const sorted = [...products];
    if (order === "name") sorted.sort((a, b) => a.name.localeCompare(b.name));
    return sorted;
  }, [products, order]);

  // ── Labels filtres ───────────────────────────────────────────────────────────

  const periodLabel: Record<string, string> = { "7": "7 jours", "30": "30 jours", "90": "90 jours" };
  const orderLabel: Record<string, string> = {
    recent: "Derniers ajouts",
    oldest: "Plus anciens",
    name: "Nom",
  };

  // ── Rendu ────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-semibold">Gestion des produits</h1>
        <div className="py-12 text-center text-muted-foreground">
          Chargement des produits…
        </div>
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-semibold">Gestion des produits</h1>
        <div className="rounded-lg border border-border bg-card p-6 text-center">
          <p className="text-foreground">{error}</p>
          <Button className="mt-4" onClick={loadProducts}>
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-semibold">Gestion des produits</h1>

      {/* Bandeau d'erreur non-bloquant */}
      {error && (
        <div className="flex items-center justify-between rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <span>{error}</span>
          <button className="ml-4 underline underline-offset-2" onClick={() => setError(null)}>
            Fermer
          </button>
        </div>
      )}

      {/* Barre de recherche */}
      <div className="flex items-center gap-2">
        <input
          placeholder="Rechercher un produit à partir de son nom"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={`flex-1 ${inputCls}`}
        />
        <Button size="default">
          <Search size={16} />
        </Button>
      </div>

      {/* Filtres + bouton Ajouter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Filtre période */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                {periodLabel[period]} <ChevronDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {(["7", "30", "90"] as const).map((p) => (
                <DropdownMenuItem key={p} onClick={() => setPeriod(p)}>
                  {periodLabel[p]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Filtre ordre */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                {orderLabel[order]} <ChevronDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {(["recent", "oldest", "name"] as const).map((o) => (
                <DropdownMenuItem key={o} onClick={() => setOrder(o)}>
                  {orderLabel[o]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Button onClick={openAdd} className="gap-2">
          <Plus size={16} /> Ajouter un produit
        </Button>
      </div>

      {/* Tableau AG-Grid avec style shadcn/ui */}
      <div
        className="ag-theme-quartz w-full overflow-hidden rounded-lg border border-border"
        style={AG_SHADCN_VARS}
      >
        <AgGridReact<Product>
          ref={gridRef}
          rowData={rowData}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          quickFilterText={query}
          context={gridContext}
          domLayout="autoHeight"
          animateRows
          pagination
          paginationPageSize={20}
          rowHeight={56}
          headerHeight={48}
          suppressMovableColumns
          noRowsOverlayComponent={() => (
            <span className="text-muted-foreground text-sm">Aucun produit</span>
          )}
        />
      </div>

      {/* Modal ajout / édition */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div
            className="w-[640px] max-w-[95vw] rounded-lg border border-border bg-card p-6 shadow-lg space-y-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold text-card-foreground">
              {editingProduct ? "Modifier le produit" : "Ajouter un produit"}
            </h2>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Nom</label>
                <input
                  className={inputCls}
                  value={draft.nom}
                  onChange={(e) => setDraft({ ...draft, nom: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Type</label>
                <select
                  className={selectCls}
                  value={draft.type}
                  onChange={(e) => setDraft({ ...draft, type: e.target.value })}
                >
                  <option value="">Sélectionner…</option>
                  {typesOptions.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Catégorie</label>
                <select
                  className={selectCls}
                  value={draft.categorie}
                  onChange={(e) => setDraft({ ...draft, categorie: e.target.value })}
                >
                  <option value="">Sélectionner…</option>
                  {categoriesOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Unité</label>
                <select
                  className={selectCls}
                  value={draft.unite}
                  onChange={(e) => setDraft({ ...draft, unite: e.target.value })}
                >
                  <option value="">Sélectionner…</option>
                  {unitesOptions.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Quantité</label>
                <input
                  type="number"
                  className={inputCls}
                  value={draft.quantite}
                  onChange={(e) => setDraft({ ...draft, quantite: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Prix achat (FCFA)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1}
                  className={inputCls}
                  value={draft.prixAchat}
                  onChange={(e) => setDraft({ ...draft, prixAchat: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Prix vente (FCFA)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1}
                  className={inputCls}
                  value={draft.prixVente}
                  onChange={(e) => setDraft({ ...draft, prixVente: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Date MAJ</label>
                <input
                  className={inputCls}
                  value={draft.dateMaj}
                  onChange={(e) => setDraft({ ...draft, dateMaj: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Date de péremption
                  <span className="ml-1 text-muted-foreground font-normal">(optionnel)</span>
                </label>
                <input
                  type="date"
                  className={inputCls}
                  value={draft.datePeremption}
                  onChange={(e) => setDraft({ ...draft, datePeremption: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={closeModal}>
                Annuler
              </Button>
              <Button onClick={saveModal} disabled={saving}>
                {saving
                  ? editingProduct ? "Enregistrement…" : "Ajout…"
                  : editingProduct ? "Enregistrer" : "Ajouter"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
